import styled from "@emotion/styled";
import { useField } from "formik";
import { useMemo } from "react";

import {
  AdornmentIcon as AdornmentIconContainer,
  DEFAULT_INPUT_HEIGHT,
  FieldSelect,
  SkiliticsIcon,
  Tooltip,
  useFieldContext,
  type FieldSelectProps,
} from "@skilitics/designer-ui-components";

import type { LengthBehavior } from "../types";

const StyledFieldSelect = styled(FieldSelect)`
  height: ${DEFAULT_INPUT_HEIGHT}px;
`;

const lengthOptions: Array<{ label: string; value: LengthBehavior }> = [
  {
    label: "Fixed",
    value: "fixed",
  },
  {
    label: "Fit Content",
    value: "fit",
  },
  {
    label: "Fill Space",
    value: "fill",
  },
];

type Props = Omit<FieldSelectProps, "options"> & {
  orientation: "horizontal" | "vertical";
  enableFitContent: boolean;
};

export const LengthBehaviorSelect = ({ orientation, enableFitContent, tooltip, ...props }: Props) => {
  const { name } = useFieldContext();
  const [{ value }] = useField<string>(name);

  const AdornmentIcon = useMemo(() => {
    let icon =
      orientation === "horizontal" ? (
        <SkiliticsIcon icon="container-fill" rotation={90} />
      ) : (
        <SkiliticsIcon icon="container-fill" />
      );
    switch (value) {
      case "fixed":
        icon =
          orientation === "horizontal" ? (
            <SkiliticsIcon icon="container-fixed-size" rotation={90} />
          ) : (
            <SkiliticsIcon icon="container-fixed-size" />
          );
        break;
      case "fit":
        icon =
          orientation === "horizontal" ? (
            <SkiliticsIcon icon="container-fit-content" rotation={90} />
          ) : (
            <SkiliticsIcon icon="container-fit-content" />
          );
        break;
    }
    return icon;
  }, [orientation, value]);

  const options = useMemo(
    () => lengthOptions.filter((option) => !(!enableFitContent && option.value === "fit")),
    [enableFitContent],
  );

  return (
    <StyledFieldSelect
      {...props}
      variant="standard"
      startAdornment={
        <Tooltip title={tooltip}>
          <AdornmentIconContainer>{AdornmentIcon}</AdornmentIconContainer>
        </Tooltip>
      }
      options={options}
    />
  );
};
